/**
 * Pure, keyless map-URL builders for the venue location surface (`venue-address-and-map`, R4).
 *
 * The maps decision (locked): a consent-free OpenStreetMap iframe embed + keyless Google Maps
 * link-outs. No SDK, no API key, no third-party cookie — so a venue-site static build needs no
 * secrets and the staff SPA can render directions/links from the pin alone. A venue WITHOUT a Google
 * place ref still gets a working embed + directions ([placeLinkUrl] is the only ref-gated builder).
 *
 * (The draggable Leaflet pin picker `@repo/maps` named in the task is a follow-up; these pure
 * builders are the half every surface needs and carry no vendor dependency.)
 */

export interface LatLng {
  lat: number;
  lng: number;
}

/** Half-width of the embed bounding box, in degrees (~220 m latitude) — a tight street-level view. */
const BBOX_DELTA = 0.002;

/**
 * A consent-free OSM `export/embed.html` iframe URL centred on [geo] with a marker. Loaded only
 * AFTER an explicit click (the consent-gated pattern) so no request reaches openstreetmap.org until
 * the user asks — the same iframe URL `tasks/privacy-and-consent.md` later gates on the public site.
 */
export function osmEmbedUrl(geo: LatLng): string {
  const minLng = geo.lng - BBOX_DELTA;
  const minLat = geo.lat - BBOX_DELTA;
  const maxLng = geo.lng + BBOX_DELTA;
  const maxLat = geo.lat + BBOX_DELTA;
  const bbox = `${minLng},${minLat},${maxLng},${maxLat}`;
  const marker = `${geo.lat},${geo.lng}`;
  return `https://www.openstreetmap.org/export/embed.html?bbox=${encodeURIComponent(
    bbox,
  )}&layer=mapnik&marker=${encodeURIComponent(marker)}`;
}

/** A full-page OSM link (link-out) centred on the pin — the "view larger map" escape hatch. */
export function osmLinkUrl(geo: LatLng, zoom = 18): string {
  return `https://www.openstreetmap.org/?mlat=${geo.lat}&mlon=${geo.lng}#map=${zoom}/${geo.lat}/${geo.lng}`;
}

/**
 * A keyless Google Maps directions link to the pin (`/maps/dir/?api=1&destination=lat,lng`). Uses
 * the authoritative coordinates (TN-7), never geocoded text, so directions always point at the
 * confirmed entrance.
 */
export function directionsUrl(geo: LatLng): string {
  return `https://www.google.com/maps/dir/?api=1&destination=${geo.lat},${geo.lng}`;
}

/**
 * A keyless Google Maps place link (reviews/details extra) — produced ONLY when a Google place ref
 * exists (R4). `query` falls back to the pin so the link still resolves if Google can't match the id.
 */
export function placeLinkUrl(geo: LatLng, placeId: string): string {
  return `https://www.google.com/maps/search/?api=1&query=${geo.lat},${geo.lng}&query_place_id=${encodeURIComponent(
    placeId,
  )}`;
}
